import { useQuery } from '@tanstack/react-query';

import { createQueryOptions } from '../../query';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import {
  StoreCategoriesResponse,
  StoreCategoryResponse,
  StoreCategorySearch,
} from './category.types';

const useListCategories = <T = StoreCategoriesResponse>({
  causeId,
  select,
  queryOptions,
}: {
  causeId: string;
  select?: (data?: StoreCategoriesResponse) => T;
  queryOptions?: StoreCategorySearch;
}) => {
  const StoreApi = useStoreApi();

  const { queryString, queryObject, queryPagination } = createQueryOptions<StoreCategorySearch>({
    search: queryOptions,
    existingSearchString: `cause_id=${causeId}`,
  });

  const getCategories = async () => {
    const { data } = await StoreApi.get<StoreCategoriesResponse>(`/categories?${queryString}`);
    return data;
  };

  const queryKey = causeId ? storeKeys.id(causeId).categories.all : [];

  return useQuery({
    queryFn: getCategories,
    queryKey: [...queryKey, queryObject, queryPagination],
    meta: { queryString },
    select,
    enabled: !!causeId,
  });
};

/**
 * This endpoint doesn't return categories that don't have an active item, so creating this hook to always return all categories
 */
const useListAllCategories = <T = StoreCategoriesResponse>({
  causeId,
  select,
  queryOptions,
}: {
  causeId: string;
  select?: (data?: StoreCategoriesResponse) => T;
  queryOptions?: StoreCategorySearch;
}) => {
  const StoreApi = useStoreApi();

  const { queryString, queryObject, queryPagination } = createQueryOptions<StoreCategorySearch>({
    search: queryOptions,
    existingSearchString: `cause_id=${causeId}&search[all][value]=true`,
  });

  const getCategories = async () => {
    const { data } = await StoreApi.get<StoreCategoriesResponse>(`/categories?${queryString}`);
    return data;
  };

  const queryKey = causeId ? storeKeys.id(causeId).categories.allIncludingUnused : [];

  return useQuery({
    queryFn: getCategories,
    queryKey: [...queryKey, queryObject, queryPagination],
    meta: { queryString },
    select,
    enabled: !!causeId,
  });
};

const useShowCategory = ({
  causeId,
  categoryId,
  enabled = true,
}: {
  causeId: string;
  categoryId?: string;
  enabled?: boolean;
}) => {
  const StoreApi = useStoreApi();

  const getCategory = async () => {
    const { data } = await StoreApi.get<StoreCategoryResponse>(
      `/categories/${categoryId}?cause_id=${causeId}`
    );
    return data.category;
  };

  const queryKey = storeKeys.id(causeId).categories.id(categoryId).detail;

  return useQuery({
    queryFn: getCategory,
    queryKey: [...queryKey],
    enabled: !!causeId && !!categoryId && enabled,
  });
};

export { useListAllCategories, useListCategories, useShowCategory };
